import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Briefcase, Camera, Edit, Save, X, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { DailyHoroscope } from '../../components/DailyHoroscope';
import { AddToHomeScreen } from '../../components/AddToHomeScreen';
import { DailyVideo } from '../../components/DailyVideo';
import { compressImageSafe, uploadErrorMessage } from '../../utils/imageCompression';

import { getBackendUrl } from '../../utils/getBackendUrl';
import { PersonalFileStorage } from '../../components/PersonalFileStorage';
import MyBadgesWidget from '../../components/MyBadgesWidget';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EmployeeProfile = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [timeLogs, setTimeLogs] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    address: user.address || '',
    position: user.position || '',
    department: user.department || '',
    hire_date: user.hire_date || '',
    skills: user.skills || []
  });

  useEffect(() => {
    fetchTimeLogs();
  }, []);

  const fetchTimeLogs = async () => {
    try {
      const response = await axios.get(`${API}/timelog`);
      setTimeLogs(response.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch time logs');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/profile`, formData);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleUploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      toast.info('Compressing image...');
      const compressedFile = await compressImageSafe(file, 512, 512, 0.85);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      await axios.post(`${API}/users/upload-profile-picture`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      onUpdate();
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skillToRemove) });
  };

  const totalHours = timeLogs.reduce((sum, log) => sum + (log.hours || 0), 0);

  return (
    <div className="space-y-6">
      {/* Professional Header Banner */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 p-8 shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.1),transparent)]"></div>
        <div className="relative flex items-center gap-4">
          <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Employee Profile</h1>
            <p className="text-white/90 text-lg">Manage your work information and professional details</p>
          </div>
        </div>
      </div>

      {/* Add to Home Screen */}
      <AddToHomeScreen />

      {/* Daily Horoscope */}
      <DailyHoroscope />

      {/* Daily Video */}
      <DailyVideo />

      {/* Badges */}
      <MyBadgesWidget />

      {/* Professional Profile Card */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
        <div className="p-8">
          <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
            {/* Enhanced Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
                <Avatar className="relative w-36 h-36 ring-4 ring-white/10">
                  <AvatarImage src={user.profile_picture ? `${BACKEND_URL}${user.profile_picture}` : undefined} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-4xl font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <label
                  htmlFor="employee-profile-picture"
                  className="absolute bottom-0 right-0 bg-gradient-to-r from-blue-500 to-blue-600 p-3 rounded-full cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg transform hover:scale-110"
                >
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    id="employee-profile-picture"
                    type="file"
                    accept="image/*"
                    onChange={handleUploadPicture}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-white">{user.name}</h2>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-400/30 text-blue-400 rounded-full text-sm font-semibold backdrop-blur-sm">
                  <Briefcase className="w-4 h-4" />
                  <span>{formData.position || 'Employee'}</span>
                </div>
                {formData.department && (
                  <div className="text-slate-400 text-sm">
                    <span className="font-medium">Department:</span> {formData.department}
                  </div>
                )}
              </div>
            </div>

          {/* Details Section */}
          <div className="flex-1">
            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position" className="text-slate-300">Position</Label>
                    <Input
                      id="position"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder="e.g., Project Manager"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="department" className="text-slate-300">Department</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g., Construction"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hire_date" className="text-slate-300">Hire Date</Label>
                    <Input
                      id="hire_date"
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-slate-300">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <Label className="text-slate-300 mb-2 block">Skills & Certifications</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Add a skill"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    />
                    <Button type="button" onClick={addSkill} size="sm">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-2">
                        {skill}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-red-400"
                          onClick={() => removeSkill(skill)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-gradient-to-r from-blue-500 to-blue-600">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Email Address</p>
                    <p className="text-slate-900 font-semibold text-lg break-all">{user.email}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Position</p>
                    <p className="text-slate-900 font-semibold text-lg">{user.position || 'Not specified'}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Department</p>
                    <p className="text-slate-900 font-semibold text-lg">{user.department || 'Not specified'}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Hire Date</p>
                    <p className="text-slate-900 font-semibold text-lg">{user.hire_date ? new Date(user.hire_date).toLocaleDateString('en-US', { timeZone: 'America/Toronto', year: 'numeric', month: 'long', day: 'numeric' }) : 'Not specified'}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Phone Number</p>
                    <p className="text-slate-900 font-semibold text-lg">{user.phone || 'Not provided'}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                    <p className="text-slate-600 text-sm font-medium mb-2">Address</p>
                    <p className="text-slate-900 font-semibold text-lg">{user.address || 'Not provided'}</p>
                  </div>
                </div>

                {/* Enhanced Skills Section */}
                {user.skills && user.skills.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-5 border border-blue-400/20">
                    <p className="text-blue-300 text-sm font-semibold mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                      Skills & Certifications
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {user.skills.map((skill, index) => (
                        <Badge key={index} className="bg-blue-500/20 text-blue-300 border-blue-400/30 hover:bg-blue-500/30 px-3 py-1">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Enhanced Work Summary */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl border border-slate-700/50 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
          <h3 className="text-2xl font-bold text-white">Work Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="relative overflow-hidden p-6 bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl border border-blue-400/30 hover:border-blue-400/50 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-400/10 rounded-full -mr-10 -mt-10"></div>
            <p className="text-blue-300 text-sm font-medium mb-2">Total Hours Logged</p>
            <p className="text-4xl font-bold text-white">{totalHours.toFixed(1)}</p>
            <p className="text-blue-400 text-sm mt-1">hours</p>
          </div>
          <div className="relative overflow-hidden p-6 bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl border border-purple-400/30 hover:border-purple-400/50 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-400/10 rounded-full -mr-10 -mt-10"></div>
            <p className="text-purple-300 text-sm font-medium mb-2">Recent Shifts</p>
            <p className="text-4xl font-bold text-white">{timeLogs.length}</p>
            <p className="text-purple-400 text-sm mt-1">time entries</p>
          </div>
          <div className="relative overflow-hidden p-6 bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl border border-green-400/30 hover:border-green-400/50 transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-400/10 rounded-full -mr-10 -mt-10"></div>
            <p className="text-green-300 text-sm font-medium mb-2">Average Hours/Shift</p>
            <p className="text-4xl font-bold text-white">{timeLogs.length > 0 ? (totalHours / timeLogs.length).toFixed(1) : '0'}</p>
            <p className="text-green-400 text-sm mt-1">hours per shift</p>
          </div>
        </div>

        {/* Recent Time Logs */}
        {timeLogs.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold text-white mb-3">Recent Time Logs</h4>
            <div className="space-y-2">
              {timeLogs.map((log) => (
                <div key={log.id} className="p-3 bg-slate-800/50 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="text-white font-semibold">
                      {new Date(log.clock_in).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                    </p>
                    <p className="text-sm text-slate-400">
                      {new Date(log.clock_in).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })} - {log.clock_out ? new Date(log.clock_out).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' }) : 'In Progress'}
                    </p>
                  </div>
                  {log.hours && (
                    <span className="text-blue-400 font-bold">{log.hours} hrs</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Password Change Section */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl border border-slate-700/50 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
          <h3 className="text-2xl font-bold text-white">Change Password</h3>
        </div>
        
        <form onSubmit={async (e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const currentPassword = formData.get('current_password');
          const newPassword = formData.get('new_password');
          const confirmPassword = formData.get('confirm_password');
          
          if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
          }
          
          try {
            await axios.post(`${API}/users/change-password`, {
              current_password: currentPassword,
              new_password: newPassword
            });
            toast.success('Password changed successfully');
            e.target.reset();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
          }
        }} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="current_password" className="text-slate-300">Current Password</Label>
            <Input
              type="password"
              id="current_password"
              name="current_password"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="new_password" className="text-slate-300">New Password</Label>
            <Input
              type="password"
              id="new_password"
              name="new_password"
              required
              minLength={6}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="confirm_password" className="text-slate-300">Confirm New Password</Label>
            <Input
              type="password"
              id="confirm_password"
              name="confirm_password"
              required
              minLength={6}
              className="mt-1"
            />
          </div>
          <Button type="submit" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
            Change Password
          </Button>
        </form>
      </div>

      {/* Personal File Storage */}
      <PersonalFileStorage user={user} />
    </div>
  );
};

export default EmployeeProfile;